/**
 * Soʻz jangi — 1 ga 1, 7 raund (demo «Lexo v3» `DuelMenu`/`DuelFriend`/`DuelRandom`/`DuelBot`/`duelVs`/`duelGame`).
 * Doʻst va tasodifiy raqib — server (lexo-api) navbatma-navbat jangi: har kim oʻz vaqtida oʻynaydi,
 * raqib allaqachon oʻynagan boʻlsa uning javoblari «jonli» qayta oʻynaladi (ghost). Bot — toʻliq mijozda.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { openTelegramLink } from '@telegram-apps/sdk-react';
import { W, distract, loadWords, shuffle, wordById } from '../../data';
import type { Word } from '../../data/types';
import { S, bump, started, useStore } from '../../store';
import { nav } from '../../lib/nav';
import { ApiError } from '../../lib/api';
import { applyCourse, fire, sync, type ApiDuel, type ApiPlayer, type DuelAns } from '../../lib/sync';
import { boop, chime } from '../../lib/sound';
import { haptic } from '../../lib/telegram';
import { toast } from '../../lib/toast';
import { XPR, estXp } from '../../lib/xp';
import { t, tn } from '../../i18n';
import { Ic } from '../../ui/Icon';
import { BotAvatar, LetterAvatar, MyAvatar, PlayerAvatar } from '../../ui/Avatar';

const R = 7;            // raundlar soni
const ROUND_MS = 10000; // raund vaqti
const BOT_USER = (import.meta.env.VITE_BOT_USERNAME as string | undefined) || 'lexocard_bot';

type Res = 'win' | 'draw' | 'loss';
type Mode = 'friend' | 'random' | 'bot';
type BotLvl = { k: 'easy' | 'mid' | 'hard'; n: string; acc: number; spd: number };
const L3: BotLvl[] = [
  { k: 'easy', n: 'Oson', acc: 0.6, spd: 6 },
  { k: 'mid', n: 'Oʻrta', acc: 0.8, spd: 4 },
  { k: 'hard', n: 'Qiyin', acc: 0.95, spd: 2.5 },
];

/** Raqib: bot (ehtimollik bilan), ghost (yozib olingan javoblar) yoki hali yoʻq (yakka oʻyin). */
type Opp =
  | { kind: 'bot'; lvl: BotLvl }
  | { kind: 'ghost'; player: ApiPlayer; ans: DuelAns[] }
  | { kind: 'none'; player: ApiPlayer | null };

interface GameCfg { mode: Mode; words: Word[]; opp: Opp; duel?: ApiDuel; bot?: BotLvl }
interface GameOut { answers: DuelAns[]; me: number; opp: number; ok: number }

type Phase =
  | { p: 'menu' }
  | { p: 'loading' }
  | { p: 'friend'; duel: ApiDuel }
  | { p: 'search' }
  | { p: 'noopp'; duel: ApiDuel }
  | { p: 'botpick' }
  | { p: 'vs'; cfg: GameCfg }
  | { p: 'game'; cfg: GameCfg; key: number }
  | { p: 'botEnd'; cfg: GameCfg; out: GameOut; res: Res; xp: number }
  | { p: 'duelEnd'; cfg?: GameCfg; duel: ApiDuel | null; out?: GameOut; xp: number; err?: boolean };

/** Raund ochkosi (server `DuelGain` bilan bir xil). */
const gain = (a: DuelAns | null) => (a && a.ok && a.ms < ROUND_MS ? 10 + Math.ceil(10 * (1 - a.ms / ROUND_MS)) : 0);
const resOf = (me: number, opp: number): Res => (me > opp ? 'win' : me < opp ? 'loss' : 'draw');
const pname = (p: ApiPlayer | null | undefined) => (p ? p.nick || p.n || t('Raqib') : t('Raqib'));
/** Demo `battlePool()`: boshlangan soʻzlar (≥12) yoki butun roʻyxat. */
function battlePool(): Word[] { const s = started(); return s.length >= 12 ? s : W; }

/** Server soʻz id'lari → Word (ilova roʻyxati + serverdan yuklanganlar); topilmasa — tasodifiy soʻz (xato emas). */
function duelWords(ids: number[]): Word[] {
  const got = ids.map((id) => wordById(id) ?? null);
  const spare = shuffle(W.filter((w) => !got.includes(w)));
  const out = got.map((w) => w ?? spare.pop() ?? W[0]!);
  while (out.length < R) out.push(spare.pop() ?? W[0]!);
  return out.slice(0, R);
}

/** Server jangidan oʻyin sozlamasi (raqib oʻynagan boʻlsa — ghost). */
function duelCfg(d: ApiDuel): GameCfg {
  const opp: Opp = d.opp.player && d.opp.ans?.length ? { kind: 'ghost', player: d.opp.player, ans: d.opp.ans } : { kind: 'none', player: d.opp.player };
  return { mode: d.mode, words: duelWords(d.words), opp, duel: d };
}

/** `?start=` — bot /start orqali Mini App tugmasini yuboradi (`?startapp=` faqat BotFather'da Main Mini App yoqilganda ishlaydi). */
function shareLink(code: string) { return `https://t.me/${BOT_USER}?start=duel_${code}`; }

/** Taklif havolasini Telegram orqali ulashish; imkon boʻlmasa — nusxalash. */
function shareDuel(code: string) {
  const link = shareLink(code);
  const text = `${t('Soʻz jangida men bilan bellashing!')} ${t('Taklif kodi')}: ${code}`;
  try {
    if (openTelegramLink.isAvailable()) { openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`); return; }
  } catch { /* nusxalashga */ }
  try { navigator.clipboard.writeText(link).then(() => toast(t('Havola nusxalandi')), () => toast(link)); } catch { toast(link); }
}

function errToast(e: unknown) {
  if (e instanceof ApiError && e.status === 409) toast(t('Bu jang band'));
  else if (e instanceof ApiError && e.status === 404) toast(t('Jang topilmadi'));
  else toast(t('Server bilan aloqa yoʻq'));
}

/** Raqib avatari (oʻlchami bilan). */
function OppAv({ opp, size }: { opp: Opp; size: number }) {
  if (opp.kind === 'bot') return <BotAvatar size={size} />;
  if (opp.player) return <PlayerAvatar p={opp.player} size={size} />;
  return <LetterAvatar n="?" c="#9aa3b5" size={size} />;
}
const oppName = (opp: Opp) => (opp.kind === 'bot' ? 'Lexo bot' : pname(opp.player));

export function Duel({ code }: { code?: string }) {
  const [ph, setPh] = useState<Phase>(code ? { p: 'loading' } : { p: 'menu' });
  const lvl = useStore((s) => s.lvl);
  const alive = useRef(true);
  const gameKey = useRef(0);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { void loadWords(); }, []); // do'st jangi (polling) uchun oldindan
  const go = (p: Phase) => { if (alive.current) setPh(p); };
  const menu = () => go({ p: 'menu' });

  /** Server jangini holatiga qarab ochish. */
  const openDuel = (d: ApiDuel) => {
    if (d.me.ans) go({ p: 'duelEnd', duel: d, xp: 0 });                         // allaqachon oʻynagan
    else if (d.role === 'creator' && !d.opp.player) go({ p: 'friend', duel: d }); // oʻz taklifi, hali hech kim yoʻq
    else if (d.opp.player) go({ p: 'vs', cfg: duelCfg(d) });
    else go({ p: 'noopp', duel: d });
  };

  // Havola orqali kelgan jang
  useEffect(() => {
    if (!code) return;
    sync.duelGet(code).then((d) => loadWords().then(() => d)).then(openDuel, (e: unknown) => { errToast(e); menu(); });
  }, [code]); // eslint-disable-line

  const startFriend = () => {
    go({ p: 'loading' });
    sync.duelCreate('friend').then((d) => go({ p: 'friend', duel: d }), (e: unknown) => { errToast(e); menu(); });
  };
  const startRandom = () => {
    go({ p: 'search' });
    const min = new Promise((r) => setTimeout(r, 1500 + Math.random() * 1000));
    Promise.all([sync.duelCreate('random'), min, loadWords()]).then(([d]) => {
      if (!alive.current) return;
      if (d.opp.player) go({ p: 'vs', cfg: duelCfg(d) }); else go({ p: 'noopp', duel: d });
    }, (e: unknown) => { errToast(e); menu(); });
  };
  const startBot = (lvl: BotLvl) => go({ p: 'vs', cfg: { mode: 'bot', words: shuffle(battlePool()).slice(0, R), opp: { kind: 'bot', lvl }, bot: lvl } });
  const play = (cfg: GameCfg) => go({ p: 'game', cfg, key: ++gameKey.current });
  const rematch = (cfg?: GameCfg) => {
    if (!cfg || cfg.mode === 'friend') startFriend();
    else if (cfg.mode === 'random') startRandom();
    else if (cfg.bot) startBot(cfg.bot);
  };

  /** Natijani serverga yuborish (doʻst/tasodifiy). */
  const submit = (cfg: GameCfg, out: GameOut) => {
    const d = cfg.duel!;
    go({ p: 'duelEnd', cfg, duel: null, out, xp: 0 });
    sync.duelSubmit(d.code, out.answers).then((r) => {
      const xp = r.xp ?? 0;
      S().set({ xp: S().xp + xp });
      if (r.status === 'done' && r.result) { const du = S().duels; S().set({ duels: { ...du, [r.result]: du[r.result] + 1 } }); }
      go({ p: 'duelEnd', cfg, duel: r, out, xp });
    }, () => { toast(t('Natijani yuborib boʻlmadi')); go({ p: 'duelEnd', cfg, duel: null, out, xp: 0, err: true }); });
  };

  const onEnd = (cfg: GameCfg, out: GameOut) => {
    S().set({ right: S().right + out.ok, wrong: S().wrong + (R - out.ok) });
    if (cfg.mode !== 'bot') return submit(cfg, out);
    const res = resOf(out.me, out.opp);
    const est = estXp(XPR.duel[res] + out.ok * XPR.gameAns);
    bump(est);
    const du = S().duels; S().set({ duels: { ...du, [res]: du[res] + 1 } });
    if (res === 'win') chime();
    go({ p: 'botEnd', cfg, out, res, xp: est });
    fire(sync.game({ game: 'duel_bot', score: out.ok, result: res }).then((r) => {
      applyCourse(r.course);
      setPh((cur) => (cur.p === 'botEnd' && cur.out === out ? { ...cur, xp: r.xp } : cur));
    }), 'duel_bot');
  };

  switch (ph.p) {
    case 'menu': return <Menu onPick={(m) => (m === 'friend' ? startFriend() : m === 'random' ? startRandom() : go({ p: 'botpick' }))} />;
    case 'loading': return <Wait title="Soʻz jangi" onBack={menu}><div className="radar scan"><MyAvatar size={72} /></div><span className="t13">{t('Yuklanmoqda…')}</span></Wait>;
    case 'friend': return <FriendWait duel={ph.duel} onBack={menu} onPlay={(d) => play(duelCfg(d))} />;
    case 'search': return (
      <Wait title="Tasodifiy raqib" onBack={menu}>
        <div className="radar scan"><MyAvatar size={72} /></div>
        <b className="t16b" style={{ fontSize: 20 }}>{t('Raqib qidirilmoqda…')}</b>
        <span className="t13">{lvl} {t('darajadagi onlayn oʻyinchilar orasidan')}</span>
      </Wait>
    );
    case 'noopp': return (
      <Wait title="Tasodifiy raqib" onBack={menu}>
        <div className="radar"><MyAvatar size={72} /></div>
        <b className="t16b" style={{ fontSize: 20 }}>{t('Hozir onlayn raqib yoʻq')}</b>
        <span className="t13">{t('Oʻynang — natijangizni keyingi raqib koʻradi, natija kelganda xabar beramiz.')}</span>
        <button className="btn" style={{ width: '100%' }} onClick={() => play(duelCfg(ph.duel))}><Ic n="play" size={18} />{t('Oʻynash')}</button>
      </Wait>
    );
    case 'botpick': return (
      <Wait title="Lexo bot" onBack={menu}>
        <div className="radar"><BotAvatar size={72} /></div>
        <b className="t16b" style={{ fontSize: 20 }}>{t('Bot qiyinligini tanlang')}</b>
        <div className="col g10" style={{ width: '100%' }}>
          {L3.map((l) => (
            <button key={l.k} className="qa2" onClick={() => startBot(l)}>
              <span className="grow col g4" style={{ textAlign: 'left' }}>
                <b className="t15b">{t(l.n)}</b>
                <span className="t13">{tn('Aniqlik ~#% · javob ~# soniyada', Math.round(l.acc * 100), String(l.spd).replace('.', ','))}</span>
              </span>
              <span className="chev2"><Ic n="chevRight" size={16} /></span>
            </button>
          ))}
        </div>
      </Wait>
    );
    case 'vs': return <Vs cfg={ph.cfg} onGo={() => play(ph.cfg)} />;
    case 'game': return <Game key={ph.key} cfg={ph.cfg} onQuit={menu} onEnd={(out) => onEnd(ph.cfg, out)} />;
    case 'botEnd': return <BotEnd {...ph} onAgain={() => rematch(ph.cfg)} />;
    case 'duelEnd': return (
      <DuelEnd duel={ph.duel} out={ph.out} cfg={ph.cfg} xp={ph.xp} err={ph.err}
        onRetry={() => ph.cfg && ph.out && submit(ph.cfg, ph.out)}
        onUpdate={(d) => {
          if (d.status === 'done' && ph.duel?.status !== 'done' && d.result) { const du = S().duels; S().set({ duels: { ...du, [d.result]: du[d.result] + 1 } }); }
          setPh((cur) => (cur.p === 'duelEnd' ? { ...cur, duel: d } : cur));
        }}
        onAgain={() => rematch(ph.cfg ?? (ph.duel ? { mode: ph.duel.mode, words: [], opp: { kind: 'none', player: null } } : undefined))} />
    );
  }
}

/* ---------- Menyu ---------- */
function Menu({ onPick }: { onPick: (m: Mode) => void }) {
  const lvl = useStore((s) => s.lvl);
  const item = (m: Mode, icon: string, bg: string, fg: string, title: string, sub: string) => (
    <button className="qa2" onClick={() => onPick(m)}>
      <span className="sq2" style={{ background: bg, color: fg }}><Ic n={icon} /></span>
      <span className="grow col g4" style={{ textAlign: 'left' }}><b className="t15b">{t(title)}</b><span className="t13">{sub}</span></span>
      <span className="chev2"><Ic n="chevRight" size={16} /></span>
    </button>
  );
  return (
    <>
      <div className="sbar">
        <button className="x" aria-label={t('Orqaga')} onClick={() => nav.pop()}><Ic n="back" /></button>
        <b style={{ flex: 1, textAlign: 'left', color: 'var(--ink)', fontSize: 16 }}>{t('Soʻz jangi')}</b>
        <span className="soon" style={{ background: 'var(--success-soft)', color: 'var(--success-ink)' }}>{lvl}</span>
      </div>
      <div className="sbody fade" style={{ gap: 12 }}>
        <div className="duelhero">
          <Ic n="swords" size={44} />
          <div className="col g4">
            <b style={{ fontSize: 20 }}>{t('1 ga 1 bellashuv')}</b>
            <span style={{ fontSize: 13, opacity: 0.88, fontWeight: 600 }}>{tn('7 raund · tez va toʻgʻri javob yutadi · gʻalaba +# XP', XPR.duel.win)}</span>
          </div>
        </div>
        {item('friend', 'users', 'var(--primary-soft)', 'var(--primary-ink)', 'Doʻst bilan', t('Havola yuboring — doʻstingiz qoʻshiladi'))}
        {item('random', 'shuffle', 'var(--peach-soft)', 'var(--peach-ink)', 'Tasodifiy raqib', `${t('Faqat')} ${lvl} ${t('darajadagi oʻyinchilar')}`)}
        {item('bot', 'bot', 'var(--info-soft)', 'var(--info-ink)', 'Lexo bot', t('Oson, oʻrta yoki qiyin raqib'))}
        <p className="t12" style={{ margin: 0 }}>{t('Doʻst va tasodifiy raqib bilan jang navbatma-navbat: har kim oʻz vaqtida oʻynaydi, natija ikkalasi oʻynagach chiqadi.')}</p>
      </div>
    </>
  );
}

/* ---------- Kutish ekrani (demo `duelWait`) ---------- */
function Wait({ title, onBack, children }: { title: string; onBack: () => void; children: ReactNode }) {
  return (
    <>
      <div className="sbar">
        <button className="x" aria-label={t('Orqaga')} onClick={onBack}><Ic n="back" /></button>
        <b style={{ flex: 1, textAlign: 'left', color: 'var(--ink)', fontSize: 16 }}>{t(title)}</b>
      </div>
      <div className="sbody fade" style={{ alignItems: 'center', textAlign: 'center', gap: 18, paddingTop: 30 }}>{children}</div>
    </>
  );
}

/** 5 soniyada bir jang holatini yangilash (unmount'da toʻxtaydi). */
function usePoll(code: string | null, on: (d: ApiDuel) => void) {
  const cb = useRef(on); cb.current = on;
  useEffect(() => {
    if (!code) return;
    let dead = false;
    const iv = setInterval(() => { sync.duelGet(code).then((d) => { if (!dead) cb.current(d); }, () => {}); }, 5000);
    return () => { dead = true; clearInterval(iv); };
  }, [code]);
}

/* ---------- Doʻst bilan: taklif kodi ---------- */
function FriendWait({ duel, onBack, onPlay }: { duel: ApiDuel; onBack: () => void; onPlay: (d: ApiDuel) => void }) {
  const [d, setD] = useState(duel);
  usePoll(d.me.ans ? null : d.code, setD);
  const joined = d.opp.player;
  return (
    <Wait title="Doʻst bilan" onBack={onBack}>
      <div className="radar"><MyAvatar size={72} /></div>
      <b className="t16b" style={{ fontSize: 20 }}>{t('Doʻstingizni taklif qiling')}</b>
      <span className="t13">{t('Havolani yuboring va hozir oʻynang — doʻstingiz keyin sizning natijangizga qarshi oʻynaydi.')}</span>
      <div className="codebox"><span className="t12">{t('Taklif kodi')}</span><b>{d.code}</b></div>
      <button className="btn soft" style={{ width: '100%' }} onClick={() => shareDuel(d.code)}><Ic n="share" size={18} />{t('Havolani yuborish')}</button>
      <button className="btn" style={{ width: '100%' }} onClick={() => onPlay(d)}><Ic n="play" size={18} />{t('Hozir oʻynash')}</button>
      <span className="t12">
        {joined ? <b style={{ color: 'var(--success-ink)' }}>{pname(joined)} {t('qoʻshildi!')}</b> : t('Doʻstingiz kutilmoqda…')}
      </span>
    </Wait>
  );
}

/* ---------- VS ---------- */
function Vs({ cfg, onGo }: { cfg: GameCfg; onGo: () => void }) {
  const name = useStore((s) => s.nick || s.name) || t('Siz');
  const lvl = useStore((s) => s.lvl), xp = useStore((s) => s.xp);
  const go = useRef(onGo); go.current = onGo;
  useEffect(() => { const tm = setTimeout(() => go.current(), 1800); return () => clearTimeout(tm); }, []);
  const o = cfg.opp;
  const sub = o.kind === 'bot' ? t(o.lvl.n) : o.player ? `${cfg.duel?.level ?? lvl} · ${o.player.xp.toLocaleString('ru')} XP` : t('keyinroq oʻynaydi');
  return (
    <div className="sbody fade" style={{ alignItems: 'center', justifyContent: 'center', gap: 22, minHeight: '100%' }}>
      <div className="vs">
        <div className="col g8" style={{ alignItems: 'center' }}><MyAvatar size={84} /><b>{name}</b><span className="t12">{lvl} · {xp.toLocaleString('ru')} XP</span></div>
        <b className="vsx">VS</b>
        <div className="col g8" style={{ alignItems: 'center' }}><OppAv opp={o} size={84} /><b>{oppName(o)}</b><span className="t12">{sub}</span></div>
      </div>
      <span className="t13">{t('Jang boshlanmoqda…')}</span>
    </div>
  );
}

/* ---------- Oʻyin (demo `duelGame`) ---------- */
interface Rev { me: number; opp: number; meOk: boolean; oppOk: boolean }

function Game({ cfg, onQuit, onEnd }: { cfg: GameCfg; onQuit: () => void; onEnd: (o: GameOut) => void }) {
  const myName = useStore((s) => s.nick || s.name) || t('Siz');
  const opp = cfg.opp, solo = opp.kind === 'none', oName = oppName(opp);
  const [Q] = useState(() => cfg.words.map((w) => ({ w, opts: shuffle([w, ...distract(w, 'uz')]) })));
  const [qi, setQi] = useState(0);
  const [pick, setPick] = useState<number | null>(null);
  const [oppDone, setOppDone] = useState(false);
  const [rev, setRev] = useState<Rev | null>(null);
  const [left, setLeft] = useState(10);
  const [score, setScore] = useState({ me: 0, opp: 0 });
  const [hist, setHist] = useState<boolean[]>([]);
  const acc = useRef<GameOut>({ answers: [], me: 0, opp: 0, ok: 0 });
  const rd = useRef<{ t0: number; me: DuelAns | null; opp: DuelAns | null; oppIn: boolean; done: boolean; timers: ReturnType<typeof setTimeout>[]; reveal: () => void } | null>(null);
  const endRef = useRef(onEnd); endRef.current = onEnd;

  useEffect(() => {
    const r = { t0: Date.now(), me: null as DuelAns | null, opp: null as DuelAns | null, oppIn: solo, done: false, timers: [] as ReturnType<typeof setTimeout>[], reveal: () => {} };
    rd.current = r;
    let iv: ReturnType<typeof setInterval> | undefined;
    r.reveal = () => {
      if (r.done) return; r.done = true; clearInterval(iv);
      const me = r.me ?? { ok: false, ms: ROUND_MS };
      const mg = gain(me), og = solo ? 0 : gain(r.opp);
      const a = acc.current;
      a.answers.push(me); a.me += mg; a.opp += og; if (me.ok) a.ok++;
      setRev({ me: mg, opp: og, meOk: me.ok, oppOk: !!r.opp?.ok });
      setScore({ me: a.me, opp: a.opp });
      setHist((h) => [...h, mg > og || (mg === og && me.ok)]);
      if (me.ok) { chime(); } else { boop(); haptic.bad(); }
      r.timers.push(setTimeout(() => { if (qi + 1 < R) { setPick(null); setRev(null); setOppDone(false); setLeft(10); setQi(qi + 1); } else endRef.current({ ...a }); }, 1900));
    };
    // raqib javobi: bot — ehtimollik, ghost — yozib olingan vaqt
    const oppAt = (ans: DuelAns, ms: number) => r.timers.push(setTimeout(() => {
      if (r.done) return; r.opp = ans; r.oppIn = true; setOppDone(true); if (r.me) r.reveal();
    }, ms));
    if (opp.kind === 'bot') {
      const sec = Math.min(9.5, opp.lvl.spd * (0.6 + Math.random() * 0.8));
      oppAt({ ok: Math.random() < opp.lvl.acc, ms: Math.round(sec * 1000) }, sec * 1000);
    } else if (opp.kind === 'ghost') {
      const a = opp.ans[qi];
      if (a && a.ms < ROUND_MS) oppAt(a, a.ms); // vaqt tugaguncha javob bermagan boʻlsa — «oʻylayapti» qoladi
    }
    iv = setInterval(() => {
      const l = Math.max(0, 10 - Math.floor((Date.now() - r.t0) / 1000)); setLeft(l);
      if (l <= 0) r.reveal();
    }, 200);
    return () => { clearInterval(iv); r.timers.forEach(clearTimeout); r.done = true; };
  }, [qi]); // eslint-disable-line

  const choose = (i: number) => {
    const r = rd.current; const q = Q[qi]!;
    if (!r || r.me || r.done) return;
    r.me = { ok: q.opts[i]!.id === q.w.id, ms: Date.now() - r.t0 };
    setPick(i);
    if (r.oppIn) r.timers.push(setTimeout(r.reveal, 300));
  };

  const q = Q[qi]!;
  const optCls = (i: number) => {
    if (!rev) return pick === i ? 'opt sel2' : 'opt';
    if (q.opts[i]!.id === q.w.id) return 'opt ok';
    return pick === i ? 'opt sel2 bad' : 'opt dim';
  };
  const col = (ok: boolean) => (ok ? 'var(--success-ink)' : 'var(--error-ink)');

  return (
    <>
      <div className="duelbar">
        <button className="x" aria-label={t('Chiqish')} onClick={onQuit}><Ic n="x" /></button>
        <div className="dside"><MyAvatar size={40} /><div className="col"><b>{myName}</b><span>{score.me}</span></div></div>
        <span className="dtm">{left}</span>
        <div className="dside r"><div className="col" style={{ alignItems: 'flex-end' }}><b>{oName}</b><span>{solo ? '?' : score.opp}</span></div><OppAv opp={opp} size={40} /></div>
      </div>
      <div className="rdots">{Q.map((_, i) => <i key={i} className={i < hist.length ? (hist[i] ? 'w' : 'l') : i === qi ? 'on' : ''} />)}</div>
      <div className="sbody fade" key={qi} style={{ gap: 14, paddingTop: 6 }}>
        <span className="t13" style={{ textAlign: 'center', fontWeight: 700 }}>{tn('#-raund', qi + 1)}</span>
        <div className="qcard" style={{ alignItems: 'center', textAlign: 'center', padding: 20 }}>
          <b style={{ fontSize: 32, lineHeight: '40px' }}>{q.w.w}</b>
          <span className="t13">{t('Toʻgʻri tarjimani tanlang')}</span>
        </div>
        <div className="opts">
          {q.opts.map((o, i) => (
            <button key={o.id} className={optCls(i)} disabled={pick !== null || !!rev} onClick={() => choose(i)}>
              <span>{o.uz}</span>{rev && o.id === q.w.id && <Ic n="check" />}
            </button>
          ))}
        </div>
        <span className="t12" style={{ textAlign: 'center' }}>
          {rev
            ? <>{t('Siz')} <b style={{ color: col(rev.meOk) }}>+{rev.me}</b>{!solo && <> · {oName} <b style={{ color: col(rev.oppOk) }}>+{rev.opp}</b></>}</>
            : solo ? t('Raqib keyinroq oʻynaydi') : oppDone ? `${oName} ${t('javob berdi')}` : `${oName} ${t('oʻylayapti…')}`}
        </span>
      </div>
    </>
  );
}

/* ---------- Yakun ekranlari (demo `duelEnd`) ---------- */
function ResultShell({ res, title, children, foot }: { res: Res | 'wait'; title: string; children: ReactNode; foot: ReactNode }) {
  const icon = res === 'win' ? 'trophy' : res === 'draw' ? 'swords' : res === 'loss' ? 'flag' : 'timer';
  return (
    <>
      <div className="content fade" style={{ paddingTop: 36, gap: 18, alignItems: 'center', textAlign: 'center' }}>
        <div className="flamebig" style={res === 'loss' ? { background: 'var(--bg)', color: 'var(--muted)' } : res === 'wait' ? { background: 'var(--primary-soft)', color: 'var(--primary-ink)' } : undefined}><Ic n={icon} /></div>
        <h1 className="h2">{t(title)}</h1>
        {children}
      </div>
      <div className="sfoot">{foot}</div>
    </>
  );
}
const resTitle = (r: Res) => (r === 'win' ? 'Gʻalaba!' : r === 'draw' ? 'Durang' : 'Bu safar raqib yutdi');

function Score2({ left, right, ls, rs }: { left: ReactNode; right: ReactNode; ls: ReactNode; rs: ReactNode }) {
  return (
    <div className="vs small">
      <div className="col g6" style={{ alignItems: 'center' }}>{left}<b style={{ fontSize: 28 }}>{ls}</b></div>
      <b className="vsx">:</b>
      <div className="col g6" style={{ alignItems: 'center' }}>{right}<b style={{ fontSize: 28 }}>{rs}</b></div>
    </div>
  );
}

function Stat3({ xp, ok, third, thirdLabel }: { xp: number | string; ok: number; third: ReactNode; thirdLabel: string }) {
  return (
    <div className="stat3 center" style={{ width: '100%' }}>
      <div><b style={{ color: 'var(--primary-ink)' }}>+{xp}</b><span>XP</span></div>
      <div><b>{ok}/{R}</b><span>{t('toʻgʻri')}</span></div>
      <div><b style={{ color: 'var(--peach-ink)' }}>{third}</b><span>{t(thirdLabel)}</span></div>
    </div>
  );
}

function Foot({ onAgain, again = 'Revansh' }: { onAgain: () => void; again?: string }) {
  return (
    <>
      <button className="btn soft" onClick={onAgain}>{t(again)}</button>
      <button className="btn" onClick={() => nav.pop()}>{t('Oʻyinlarga')}</button>
    </>
  );
}

function BotEnd({ out, res, xp, cfg, onAgain }: { cfg: GameCfg; out: GameOut; res: Res; xp: number; onAgain: () => void }) {
  const wins = useStore((s) => s.duels.win);
  return (
    <ResultShell res={res} title={resTitle(res)} foot={<Foot onAgain={onAgain} />}>
      <Score2 left={<MyAvatar size={56} />} right={<OppAv opp={cfg.opp} size={56} />} ls={out.me} rs={out.opp} />
      <Stat3 xp={xp} ok={out.ok} third={wins} thirdLabel="gʻalabalar" />
    </ResultShell>
  );
}

function DuelEnd({ duel, out, cfg, xp, err, onRetry, onUpdate, onAgain }: {
  duel: ApiDuel | null; out?: GameOut; cfg?: GameCfg; xp: number; err?: boolean; onRetry: () => void; onUpdate: (d: ApiDuel) => void; onAgain: () => void;
}) {
  const wins = useStore((s) => s.duels.win);
  usePoll(duel?.status === 'waiting' ? duel.code : null, onUpdate);
  const myOk = duel?.me.ans ? duel.me.ans.filter((a) => a.ok).length : out?.ok ?? 0;
  const myScore = duel?.me.score ?? out?.me ?? 0;

  if (!duel) {
    return (
      <ResultShell res="wait" title={err ? 'Natija yuborilmadi' : 'Natija yuborilmoqda…'}
        foot={err ? <><button className="btn soft" onClick={() => nav.pop()}>{t('Oʻyinlarga')}</button><button className="btn" onClick={onRetry}>{t('Qayta yuborish')}</button></> : null}>
        <Score2 left={<MyAvatar size={56} />} right={cfg ? <OppAv opp={cfg.opp} size={56} /> : <LetterAvatar n="?" size={56} />} ls={myScore} rs="…" />
      </ResultShell>
    );
  }

  const oppAv = duel.opp.player ? <PlayerAvatar p={duel.opp.player} size={56} /> : <LetterAvatar n="?" c="#9aa3b5" size={56} />;
  if (duel.status === 'done' && duel.result) {
    const res = duel.result;
    return (
      <ResultShell res={res} title={resTitle(res)} foot={<Foot onAgain={onAgain} />}>
        <Score2 left={<MyAvatar size={56} />} right={oppAv} ls={duel.me.score ?? myScore} rs={duel.opp.score ?? 0} />
        <span className="t13">{t('Raqib')}: <b>{pname(duel.opp.player)}</b></span>
        <Stat3 xp={xp} ok={myOk} third={wins} thirdLabel="gʻalabalar" />
      </ResultShell>
    );
  }

  // waiting: men oʻynadim, raqib hali yoʻq
  const needFriend = duel.mode === 'friend' && !duel.opp.player;
  return (
    <ResultShell res="wait" title="Natijangiz saqlandi" foot={<Foot onAgain={onAgain} again="Yana jang" />}>
      <Score2 left={<MyAvatar size={56} />} right={oppAv} ls={myScore} rs="?" />
      <span className="t13">
        {duel.opp.player ? `${pname(duel.opp.player)} ${t('oʻynagach natija chiqadi — xabar beramiz.')}` : t('Raqibingiz oʻynagach natija chiqadi — xabar beramiz.')}
      </span>
      {needFriend && (
        <>
          <div className="codebox"><span className="t12">{t('Taklif kodi')}</span><b>{duel.code}</b></div>
          <button className="btn" style={{ width: '100%' }} onClick={() => shareDuel(duel.code)}><Ic n="share" size={18} />{t('Havolani yuborish')}</button>
        </>
      )}
      <Stat3 xp={xp} ok={myOk} third={myScore} thirdLabel="ochko" />
    </ResultShell>
  );
}
